import { useGetExportStatus } from '@/modules/exports'
import { USER_EXPORT_QUERY } from '@/modules/exports/queries'
import { downloadFile } from '@/utils'
import { useQuery } from '@apollo/client'
import { Loader } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useState } from 'react'
import { Link } from 'react-router'
// eslint-disable-next-line no-restricted-imports
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { FAIcon } from '../fa-icon'
import { TextEllipsisLoader } from '../loader'
import { Progress } from '../ui'

interface ExportToastProps {
  id: string
  exportId: string | null
}

function ExportToast({ id, exportId }: ExportToastProps) {
  const translations = useTranslate({
    link: 'components.toast.export.link',
    download: 'components.toast.export.download',

    inProgress: 'components.toast.export.in-progress',
    completed: 'components.toast.export.completed',
    failed: 'components.toast.export.failed',
  })

  const [xhrLoading, setXhrLoading] = useState(false)

  const { data } = useQuery(USER_EXPORT_QUERY, {
    skip: !exportId,
    variables: { id: exportId ?? '' },
    pollInterval: 500,
  })

  const getExportStatus = useGetExportStatus()

  const status = getExportStatus(data?.userExport)

  function handleDownload(link: string) {
    if (xhrLoading) return

    setXhrLoading(true)
    void downloadFile(link).finally(() => setXhrLoading(false))
  }

  return (
    <div className="flex flex-col gap-2">
      {status.status === 'pending' ? (
        <>
          <TextEllipsisLoader>{translations.inProgress}</TextEllipsisLoader>
          <Progress className="h-2" value={status.message} />
        </>
      ) : status.status === 'success' ? (
        <span className="flex gap-2 items-center">
          <FAIcon
            className="text-green-600"
            icon="check-circle"
            type="solid"
            fixedWidth
          />

          {translations.completed}

          <span
            className={cn(
              'group/download flex items-center',
              'text-xs text-stone-600 cursor-pointer'
            )}
            role="button"
            aria-label={translations.download}
            onClick={() => handleDownload(status.message)}
          >
            (
            <span className="flex gap-1 items-center">
              {!xhrLoading ? (
                <FAIcon icon="download" fixedWidth />
              ) : (
                <Loader.Spinner size={14} thickness={2} />
              )}
              <span className="group-hover/download:underline">
                {translations.download}
              </span>
            </span>
            )
          </span>
        </span>
      ) : (
        <span className="flex gap-2 items-center">
          <FAIcon
            className="text-red-600"
            icon="exclamation-circle"
            type="solid"
            fixedWidth
          />

          {translations.failed}
        </span>
      )}

      <div>
        <Link to="/user-exports" onClick={() => toast.dismiss(id)}>
          {translations.link}
        </Link>
      </div>
    </div>
  )
}

export function exportToast(exportId: string | null) {
  const id = `export-toast-${
    exportId ?? Math.random().toString(36).substring(2, 15)
  }`

  return toast(<ExportToast id={id} exportId={exportId} />, {
    id,
    classNames: {
      content: 'w-full',
    },
    duration: Infinity,
    closeButton: true,
  })
}
